import React from 'react'
import { formatDecimal, formatNumber } from './common'

/**
 * A ranked list with the bar drawn as the row's own background fill.
 *
 * This replaces a Chart.js horizontal bar for the "top N" sections. Those
 * counts are small integers (a screen with 5 views next to one with 1), and a
 * canvas bar chart handles that badly: it spends a fixed canvas height on ten
 * hairline bars and leaves most of the box empty, and the value has to be read
 * off an axis instead of sitting next to the label.
 *
 * Here the bar and the exact number occupy the same row, so the shape and the
 * value are readable at once, it reflows at any width without a canvas to
 * measure, and each row can be a real button when there is somewhere to go.
 */
const RankedBarList = ({ rows, color, onRowClick, valueLabel = 'views', emptyLabel }) => {
  const items = rows || []

  if (items.length === 0) {
    return <div className="small text-medium-emphasis py-3">{emptyLabel}</div>
  }

  // Scale against the largest value so the leader always fills the row. A
  // floor keeps a value of 1 from rendering as an invisible sliver.
  const max = items.reduce((m, row) => Math.max(m, Number(row.value) || 0), 0)
  const widthFor = (value) => {
    if (max <= 0) return 0
    return Math.max(4, (Number(value) / max) * 100)
  }

  return (
    <ol className="av2-ranked">
      {items.map((row, index) => {
        const interactive = Boolean(onRowClick)
        const Row = interactive ? 'button' : 'div'

        return (
          <li key={row.key ?? row.label} className="av2-ranked-item">
            <Row
              {...(interactive
                ? {
                    type: 'button',
                    onClick: () => onRowClick(row),
                    className: 'av2-ranked-row av2-ranked-row--interactive',
                  }
                : { className: 'av2-ranked-row' })}
            >
              <span
                className="av2-ranked-fill"
                style={{ width: `${widthFor(row.value)}%`, background: color }}
                aria-hidden="true"
              />

              <span className="av2-ranked-rank" aria-hidden="true">
                {index + 1}
              </span>

              <span className="av2-ranked-label" title={row.label}>
                {row.label}
                {row.sublabel && <span className="av2-ranked-sublabel">{row.sublabel}</span>}
              </span>

              <span className="av2-ranked-metrics">
                <span className="av2-ranked-value">
                  {formatNumber(row.value)}
                  <span className="av2-ranked-unit"> {valueLabel}</span>
                </span>
                {row.share !== undefined && row.share !== null && (
                  <span className="av2-ranked-share">{formatDecimal(row.share, 1)}%</span>
                )}
                {row.people !== undefined && row.people !== null && (
                  <span className="av2-ranked-people">
                    {formatNumber(row.people)}
                    <span className="av2-ranked-unit"> people</span>
                  </span>
                )}
              </span>
            </Row>
          </li>
        )
      })}
    </ol>
  )
}

export default RankedBarList
